import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '.';
import { PromptGroup, Prompt } from '@server/prompts';

export interface PromptLibraryState {
  promptGroups?: PromptGroup[];
  selectedPromptGroup: PromptGroup | null;
  selectedPrompt: Prompt | null;
  isLoading: boolean;
}

const initialState: PromptLibraryState = {
  selectedPromptGroup: null,
  selectedPrompt: null,
  isLoading: false,
};

export const initialPromptGroup: PromptGroup = {
  id: '',
  name: '',
  description: '',
  isPinned: false,
  accountId: '',
  createdAt: '',
  updatedAt: '',
  prompts: [],
};

export const initialPrompt: Prompt = {
  id: '',
  name: '',
  promptText: '',
  promptGroupId: '',
  accountId: '',
  createdAt: '',
  updatedAt: '',
};

export const promptLibrarySlice = createSlice({
  name: 'promptLibrary',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<{ isLoading: boolean }>) => {
      state.isLoading = action.payload.isLoading;
    },

    setPromptGroups: (state, action: PayloadAction<{ promptGroups: PromptGroup[] }>) => {
      state.promptGroups = action.payload.promptGroups;
      state.isLoading = false;
    },

    addEmptyPromptGroup: (state) => {
      if (!state.promptGroups) {
        state.promptGroups = [];
      }
      state.promptGroups.unshift({ ...initialPromptGroup });
    },

    removeEmptyPromptGroup: (state) => {
      if (state.promptGroups) {
        state.promptGroups = state.promptGroups.filter(
          (group) => group.id !== initialPromptGroup.id,
        );
      }
    },

    addEmptyPrompt: (state, action: PayloadAction<{ groupId: string }>) => {
      const { groupId } = action.payload;
      if (state.promptGroups) {
        const groupIndex = state.promptGroups.findIndex((group) => group.id === groupId);
        if (groupIndex !== -1) {
          state.promptGroups[groupIndex].prompts.unshift({ ...initialPrompt });
        }
      }
      state.selectedPrompt = initialPrompt;
    },

    removeEmptyPrompt: (state, action: PayloadAction<{ groupId: string }>) => {
      const { groupId } = action.payload;

      if (state.promptGroups) {
        const groupIndex = state.promptGroups.findIndex((group) => group.id === groupId);
        if (groupIndex !== -1) {
          const emptyPromptIdx = state.promptGroups[groupIndex].prompts.findIndex(
            (prompt) => prompt.id === initialPrompt.id,
          );

          if (emptyPromptIdx !== -1) {
            state.promptGroups[groupIndex].prompts.splice(emptyPromptIdx, 1);
          }
        }
      }
      state.selectedPrompt = null;
    },

    addPromptGroup: (state, action: PayloadAction<{ promptGroup: PromptGroup }>) => {
      const { promptGroup } = action.payload;
      const newInitialPromptGroup = { ...initialPrompt };
      if (!state.promptGroups) {
        state.promptGroups = [];
      }
      
      const groupIndex = state.promptGroups.findIndex(
        (group) => group.id === initialPromptGroup.id,
      );
      
      const updatedGroup = { 
        ...promptGroup, 
        prompts: promptGroup.prompts && promptGroup.prompts.length > 0 ? promptGroup.prompts : [newInitialPromptGroup] 
      };
      
      if (groupIndex !== -1) {
        state.promptGroups[groupIndex] = updatedGroup;
      } else {
        state.promptGroups.push(updatedGroup);
      }
      
      state.selectedPromptGroup = updatedGroup;
      state.selectedPrompt = newInitialPromptGroup;
    },

    updatePromptGroup: (
      state,
      action: PayloadAction<{
        id: string;
        update: Partial<PromptGroup>;
      }>,
    ) => {
      const { id, update } = action.payload;
      if (state.promptGroups) {
        const groupIndex = state.promptGroups.findIndex((group) => group.id === id);
        if (groupIndex !== -1) {
          Object.assign(state.promptGroups[groupIndex], update);
        }
      }
    },

    deletePromptGroup: (state, action: PayloadAction<{ groupId: string }>) => {
      if (state.promptGroups) {
        state.promptGroups = state.promptGroups.filter(
          (group) => group.id !== action.payload.groupId,
        );
      }
      // Clear selection if deleted group was selected
      if (state.selectedPromptGroup?.id === action.payload.groupId) {
        state.selectedPromptGroup = null;
      }
    },
    addPrompt: (state, action: PayloadAction<{ groupId: string; prompt: Prompt }>) => {
      const { groupId, prompt } = action.payload;
      if (state.promptGroups) {
        const groupIndex = state.promptGroups.findIndex((group) => group.id === groupId);
        if (groupIndex !== -1) {
          const group = state.promptGroups[groupIndex];
          const promptIndex = group.prompts.findIndex((p) => p.id === initialPrompt.id);
          if (promptIndex !== -1) {
            group.prompts[promptIndex] = prompt;
          } else {
            group.prompts.unshift(prompt);
          }

          // Update selectedPromptGroup if it's the same group
          if (state.selectedPromptGroup?.id === groupId) {
            state.selectedPromptGroup = group;
          }
        }
      }
    },
    updatePrompt: (
      state,
      action: PayloadAction<{
        groupId: string;
        promptId: string;
        update: Partial<Prompt>;
      }>,
    ) => {
      const { groupId, promptId, update } = action.payload;
      if (state.promptGroups) {
        const groupIndex = state.promptGroups.findIndex((group) => group.id === groupId);
        if (groupIndex !== -1) {
          const group = state.promptGroups[groupIndex];
          const promptIndex = group.prompts.findIndex((prompt) => prompt.id === promptId);
          if (promptIndex !== -1) {
            Object.assign(group.prompts[promptIndex], update);
          }

          // Update selectedPromptGroup if it's the same group
          if (state.selectedPromptGroup?.id === groupId) {
            state.selectedPromptGroup = group;
          }
        }
      }
    },
    deletePrompt: (state, action: PayloadAction<{ groupId: string; promptId: string }>) => {
      const { groupId, promptId } = action.payload;
      if (state.promptGroups) {
        const groupIndex = state.promptGroups.findIndex((group) => group.id === groupId);
        if (groupIndex !== -1) {
          const group = state.promptGroups[groupIndex];
          const promptIndex = group.prompts.findIndex((prompt) => prompt.id === promptId);
          if (promptIndex !== -1) {
            group.prompts.splice(promptIndex, 1);

            // If no prompts left, add initial prompt
            const isInitialPromptSet = group.prompts.length === 0;
            if (isInitialPromptSet) {
              group.prompts.push({ ...initialPrompt });
              state.selectedPrompt = initialPrompt;
            }

            // Update selectedPromptGroup if it's the same group
            if (state.selectedPromptGroup?.id === groupId) {
              state.selectedPromptGroup = group;
            }
          }
        }
      }
    },
    setSelectedPromptGroup: (state, action: PayloadAction<{ group: PromptGroup | null }>) => {
      const { group } = action.payload;
      state.selectedPromptGroup = group;

      if (!group) {
        // If no prompt group is selected, set the selected prompt to null too
        state.selectedPrompt = null;
        return;
      }

      // By product definition: If no prompts in the selected group open the prompt editor with an empty prompt
      if (group.prompts?.length === 0 && state.promptGroups) {
        const groupIndex = state.promptGroups.findIndex(
          (promptGroup) => promptGroup.id === group.id,
        );
        if (groupIndex !== -1) {
          const newPrompt = { ...initialPrompt };
          state.promptGroups[groupIndex].prompts = [newPrompt];
          state.selectedPrompt = newPrompt;
          // Update the reference in selectedPromptGroup
          state.selectedPromptGroup = state.promptGroups[groupIndex];
        }
      } else if (group.prompts?.length === 1 && group.prompts[0].id === initialPrompt.id) {
        state.selectedPrompt = group.prompts[0];
      }
    },
    setSelectedPrompt: (state, action: PayloadAction<{ prompt: Prompt | null }>) => {
      state.selectedPrompt = action.payload.prompt;
    },
    resetState: (state) => {
      state.selectedPrompt = null;
      state.selectedPromptGroup = null;
      state.isLoading = false;
    },
  },
});

export const selectPromptGroups = (state: RootState) => state.promptLibrary.promptGroups;
export const selectSelectedPromptGroup = (state: RootState) =>
  state.promptLibrary.selectedPromptGroup;
export const selectSelectedPrompt = (state: RootState) => state.promptLibrary.selectedPrompt;
export const selectIsLoading = (state: RootState) => state.promptLibrary.isLoading;

export const {
  setLoading,
  setPromptGroups,
  addEmptyPromptGroup,
  addEmptyPrompt,
  removeEmptyPrompt,
  removeEmptyPromptGroup,
  addPromptGroup,
  updatePromptGroup,
  deletePromptGroup,
  addPrompt,
  updatePrompt,
  deletePrompt,
  setSelectedPromptGroup,
  setSelectedPrompt,
  resetState,
} = promptLibrarySlice.actions;

export default promptLibrarySlice.reducer;
